import React, { forwardRef, useImperativeHandle, useEffect, useRef, useState } from 'react';
import Tooltip from '../../Tooltip';

// Chart.js draws its built-in tooltip on the canvas, so it can only ever show plain text.
// This component is an invisible anchor that follows the cursor, paired with the shared
// Tooltip's Overlay/Popper positioning, so chart hover handlers can show real HTML/JSX content.
const ChartSegmentTooltip = forwardRef((props, ref) => {
    const anchorRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [content, setContent] = useState(null);
    // Tooltip's Overlay only measures the anchor's position when it (re)mounts - moving the
    // anchor's inline style to a new segment doesn't make Popper re-measure on its own. Bumping
    // this on every show() and feeding it in as Tooltip's `index` (used as the Overlay's `key`)
    // forces a fresh mount/measurement each time, so hovering segment-to-segment without the
    // tooltip fully hiding in between still repositions it instead of leaving it stuck at the
    // first segment's location.
    const [hoverKey, setHoverKey] = useState(0);
    // Tracks which segment the tooltip is currently showing, so repeated show() calls from
    // mousemove jitter within the SAME segment don't force a remount (that's what caused the
    // flicker) - only an actual segment change (or re-showing after a hide) should.
    const lastSegmentKeyRef = useRef(null);

    // The anchor's position is only ever set imperatively from a Chart.js hover event, which
    // never fires on scroll/resize. Left alone, the anchor stays frozen in viewport space while
    // the scrolled chart moves underneath it, so the tooltip visibly drifts off the segment it
    // was labeling. Dismissing on scroll/resize is simpler and safer than trying to re-derive the
    // segment's new position without another hover event.
    useEffect(() => {
        if (!showTooltip) {
            return undefined;
        }

        const hide = () => setShowTooltip(false);
        // capture: true catches scroll on any nested scrollable ancestor too, since native
        // 'scroll' events don't bubble.
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);

        return () => {
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [showTooltip]);

    useImperativeHandle(ref, () => ({
        show(clientX, clientY, tooltipContent, segmentKey) {
            if (anchorRef.current) {
                anchorRef.current.style.left = `${clientX}px`;
                anchorRef.current.style.top = `${clientY}px`;
            }
            setContent(tooltipContent);
            setShowTooltip(true);

            // No segmentKey given falls back to always remounting (the old, safe-but-flickery
            // behavior) for any caller that hasn't been updated to pass one.
            const isNewSegment = segmentKey == null || segmentKey !== lastSegmentKeyRef.current;
            if (isNewSegment) {
                setHoverKey((key) => key + 1);
            }
            lastSegmentKeyRef.current = segmentKey ?? null;
        },
        hide() {
            setShowTooltip(false);
            lastSegmentKeyRef.current = null;
        }
    }));

    return (
        <>
            <span ref={anchorRef} style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: 0, height: 0, pointerEvents: 'none' }} />
            <Tooltip
                ref={anchorRef}
                showTooltip={showTooltip}
                customComponent={content}
                index={hoverKey}
                placement="top"
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 10]
                        }
                    }
                ]}
            />
        </>
    );
});

export default ChartSegmentTooltip;
